import { getMusicKeyForBiome } from '@/common/utils/audio';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import PhaserSceneManager, {
  getSceneManager,
} from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import { SceneLaunchContext } from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/SceneManagerTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { getBiomeFromStudentLevel } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import Login from '@/game/scenes/Login/Login';
import {} from '../Utils/PhaserUtils';

/**
 *
 * This is the main scene for the Phaser game.
 * It is responsible for initializing the game manager + scene manager and handling the persistent audio such as music and ambience.
 */
export class MainScene extends Phaser.Scene {
  constructor() {
    super('MainScene');
  }

  create() {
    // Initialize the game instance.
    if (!GameInstance.instance) {
      GameInstance.createGameInstance();
    }

    // Setup the scene manager and designate this as the main scene.
    new PhaserSceneManager(this);
    EventBus.on(
      GAME_EVENTS.ACTIVE_SCENE_CHANGED,
      this.handleActiveSceneChanged,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.USER_LOADED,
      this.handleUserLogin,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.MUSIC_TOGGLED,
      this.handleMusicToggled,
      this,
    );

    this.game.events.on(
      Phaser.Core.Events.HIDDEN,
      () => {
        setTimeout(() => {
          const gameSound =
            (this.game.sound as Phaser.Sound.WebAudioSoundManager) ||
            Phaser.Sound.HTML5AudioSoundManager;
          gameSound.context.suspend();
        }, 100);
      },
      this,
    );

    this.game.events.on(
      Phaser.Core.Events.VISIBLE,
      () => {
        setTimeout(() => {
          const gameSound =
            (this.game.sound as Phaser.Sound.WebAudioSoundManager) ||
            Phaser.Sound.HTML5AudioSoundManager;
          gameSound.context.suspend();
          gameSound.context.resume();
        }, 100);
      },
      this,
    );

    // Go to the login scene.
    getSceneManager()?.launchScene('Login', Login, { type: 'gameplay' });
  }

  updateMainMusic(levelKey: string) {
    switch (levelKey) {
      case 'LevelMap':
        // Levelmap loads the music of the biome.
        this.playLevelMusic();
        break;
      case 'Login':
        // Login screen fully resets the music.
        PhaserAudioManager.clearMusic(this);
        break;
      case 'Duel':
        PhaserAudioManager.loadAndStartMusic('duel');
        break;
      default:
        // Most scenes just pause the music.
        PhaserAudioManager.setMusicPlaying(false);
    }
  }

  playLevelMusic() {
    const student = getStudent();
    if (!student) {
      // No student found, so we can't update the music.
      return;
    }
    const key = getMusicKeyForBiome(getBiomeFromStudentLevel(student.level));
    if (!key) {
      return;
    }
    PhaserAudioManager.loadAndStartMusic(key);
    PhaserAudioManager.setMusicVolume(PhaserAudioManager.DEFAULT_MUSIC_VOLUME);
  }

  updateMainAmbience(_levelKey: string) {}

  handleActiveSceneChanged(data: {
    sceneKey: string;
    launchContext: SceneLaunchContext;
  }) {
    const { sceneKey, launchContext } = data;
    if (launchContext.type === 'gameplay') {
      // Check the music.
      this.updateMainMusic(sceneKey);
      this.updateMainAmbience(sceneKey);
    }
  }

  handleUserLogin() {
    const student = getStudent();
    if (!student) {
      return;
    }
    PhaserAudioManager.musicEnabled = student.settings.audio.musicOn;
    PhaserAudioManager.sfxEnabled = student.settings.audio.sfxOn;
  }

  handleUserLogout() {
    getSceneManager()?.launchScene('Login', Login, { type: 'gameplay' });
  }

  handleMusicToggled(newValue: boolean) {
    this.updateMainMusic(getSceneManager()?.activeSceneKey ?? '');
    PhaserAudioManager.setMusicPlaying(newValue);
  }

  destroy() {
    EventBus.off(GAME_EVENTS.USER_LOGOUT, this.handleUserLogout, this);
    this.events.off('shutdown', this.destroy, this);
  }
}
